import React, { useState, useEffect } from "react";
import { Input, Form, Switch, Upload, Button } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import moment from "moment";
import toast from "react-hot-toast";
import ConfirmationModal from "../../../components/global/modal/ConfirmationModal.jsx";
import TableWithSkeleton from "../../../components/global/table/TableWithSkeleton.jsx";
import ModalComponent from "../../../components/global/modal/ModalComponent.jsx";
import LoadableButton from "../../../components/buttons/LoadableButton.jsx";
import Edit from "../../../assets/Edit.jsx";
import {
  GetTermsAndConditionApi,
  CreateTermsAndConditionApi,
  UpdateTermsAndConditionApi,
  UpdateStatusTermsAndConditionApi,
} from "../../../api/request/tnc";
import tncColumns from "../../../columns/tnc";
import Delete from "../../../assets/Delete.jsx";
import useModulePermissions from "../../../hooks/useModulePermissions.jsx";

export default function TNC() {
  const tncPermissions = useModulePermissions("setting", "tnc");
  const canUpdate = !!tncPermissions.update;
  const [form] = Form.useForm();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editModal, setEditModal] = useState({ open: false });
  const [statusModal, setStatusModal] = useState({
    open: false,
    loading: false,
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [selectedTNC, setSelectedTNC] = useState(null);

  const beforeUpload = (file) => {
    const isPDF = file.type === "application/pdf";
    if (!isPDF) {
      toast.error("You can only upload PDF files!");
      return false;
    }
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      toast.error("File must be smaller than 10MB!");
      return false;
    }
    return false;
  };

  const getTNC = async () => {
    setLoading(true);
    try {
      const { data: apiRes } = await GetTermsAndConditionApi();
      if (apiRes?.success) {
        const rawItems = apiRes.data || [];
        const mapped = rawItems.map((item) => {
          const docs = Array.isArray(item?.termsAndConditions)
            ? item.termsAndConditions.map((d, i) => {
                const url = String(d?.fileUrl || "")
                  .replace(/[`]/g, "")
                  .trim();
                return url ? (
                  <a
                    key={d?.id || i}
                    href={url}
                    target="_blank"
                    rel="noreferrer"
                    className="primary hover:underline duration-300"
                  >
                    {d?.fileName || "Document"}
                  </a>
                ) : (
                  <span key={d?.id || i}>--</span>
                );
              })
            : [];
          return {
            ...item,
            documents:
              docs.length > 0 ? (
                <div className="flex flex-wrap gap-2">{docs}</div>
              ) : (
                "--"
              ),
            createdDate: moment(item?.createdDate).format("DD-MM-YYYY, LT"),
            status: (
              <Switch
                onChange={() => handleStatus(item)}
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={item?.isActive}
                disabled={!canUpdate}
              />
            ),
            actions: (
              <button onClick={() => openModal(item)}>
                {" "}
                <Edit />{" "}
              </button>
            ),
          };
        });
        setData(mapped);
      }
    } catch (error) {
      toast.error(error?.response?.data?.error?.message);
    } finally {
      setLoading(false);
    }
  };

  const openModal = (item) => {
    setSelectedTNC(item);
    setEditModal({ open: true });
    const initialDocs = Array.isArray(item?.termsAndConditions)
      ? item.termsAndConditions.map((d) => ({
          FileNames: d?.fileName || "",
          FileId: d?.id,
        }))
      : [];
    form.setFieldsValue({
      Title: item.title,
      Version: item.version,
      documents: initialDocs,
    });
  };

  const openAddModal = () => {
    setSelectedTNC(null);
    setEditModal({ open: true });
    form.resetFields();
    form.setFieldsValue({ documents: [{ FileNames: "" }] });
  };

  const handleSubmit = async (values) => {
    const apiHeader = {
      headers: {
        Accept: "application/json",
        "Content-Type": "multipart/form-data",
      },
    };
    setSubmitLoading(true);
    try {
      const formData = new FormData();
      formData.append("Title", values.Title);
      formData.append("Version", values.Version);
      const docs = values.documents || [];
      docs.forEach((doc) => {
        const fileName = doc?.FileNames;
        const files = doc?.Files || [];
        const fileId = doc?.FileId;
        const fileObj = files[0]?.originFileObj || files[0];
        const hasFile = files.length > 0 && !!fileObj;
        const isExistingDoc = !!fileId;
        formData.append("FileNames", fileName);
        if (isExistingDoc) formData.append("FileId", fileId);
        if (hasFile) formData.append("Files", fileObj);
      });
      let response;
      if (selectedTNC?.id) {
        response = await UpdateTermsAndConditionApi(
          selectedTNC.id,
          formData,
          apiHeader,
        );
      } else {
        response = await CreateTermsAndConditionApi(formData, apiHeader);
      }

      if (response.data?.success) {
        toast.success(response.data?.message);
        setEditModal({ open: false });
        form.resetFields();
        getTNC();
      } else {
        toast.error(response.data?.message);
      }
    } catch (error) {
      const msg =
        error?.response?.data?.error?.message ||
        error?.message ||
        "Something went wrong";
      toast.error(msg);
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleStatus = (item) => {
    setSelectedTNC(item);
    setStatusModal({ open: true, loading: false });
  };

  const confirmStatusChange = async () => {
    setStatusModal({ ...statusModal, loading: true });
    try {
      const params = { IsActive: !selectedTNC.isActive };
      const { data } = await UpdateStatusTermsAndConditionApi(
        selectedTNC.id,
        new URLSearchParams(params).toString(),
      );
      if (data?.success) {
        toast.success(data?.message);
      } else {
        toast.error(data?.message);
      }
      getTNC();
    } catch (error) {
      toast.error(error?.response?.data?.error?.message);
    } finally {
      setStatusModal({ open: false, loading: false });
    }
  };

  useEffect(() => {
    if (!editModal.open) {
      form.resetFields();
    }
  }, [editModal.open]);
  useEffect(() => {
    getTNC();
  }, []);

  return (
    <div className="mt-3">
      <div className="flex justify-end mb-4">
        {canUpdate && (
          <button
            className="export-btm flex items-center gap-2 bg-primary text-white px-6 py-2 rounded-lg"
            onClick={openAddModal}
          >
            <img src="assets/icons/add_icon.svg" alt="icon" width={12} />
            Add
          </button>
        )}
      </div>
      {/* Table */}
      <div className="border border-gray-200 rounded-xl  overflow-hidden">
        <TableWithSkeleton
          columns={tncColumns}
          data={data}
          loading={loading}
          hiddenColumns={!canUpdate ? ["Actions"] : []}
        />
      </div>

      {/* Status Confirmation Modal */}
      <ConfirmationModal
        isOpen={statusModal.open}
        setIsOpen={(open) => setStatusModal({ ...statusModal, open })}
        message="Are you sure to change the status?"
        onConfirm={confirmStatusChange}
        isLoading={statusModal.loading}
        loadingLabel="Updating status..."
      />

      {/* Add/Edit Modal */}
      <ModalComponent
        isOpen={editModal.open}
        setIsOpen={(open) => setEditModal({ open })}
        title={
          selectedTNC ? "Edit Terms & Conditions" : "Add Terms & Conditions"
        }
        width={1000}
        onCancel={() => setEditModal({ open: false })}
      >
        <Form
          requiredMark={false}
          layout="vertical"
          form={form}
          onFinish={handleSubmit}
          autoComplete="off"
          className="space-y-6"
        >
          <div className="grid grid-cols-2 gap-4 border rounded-md p-4">
            <Form.Item
              label="Title"
              name="Title"
              rules={[{ required: true, message: "Please enter title" }]}
            >
              <Input size="large" placeholder="Title" />
            </Form.Item>
            {!selectedTNC && (
              <Form.Item
                label="Version"
                name="Version"
                rules={[{ required: true, message: "Please enter version" }]}
              >
                <Input size="large" placeholder="Version" />
              </Form.Item>
            )}
          </div>
          <div className="border rounded-md p-4">
            <Form.List name="documents">
              {(fields, { add, remove }) => (
                <>
                  {fields.map((field, index) => (
                    <div
                      key={field.key}
                      className="grid sm:grid-cols-2 gap-4 mb-4"
                    >
                      <Form.Item
                        label="File Name"
                        name={[field.name, "FileNames"]}
                        fieldKey={[field.fieldKey, "FileNames"]}
                        rules={[
                          { required: true, message: "Please enter file name" },
                        ]}
                      >
                        <Input size="large" placeholder="File name" />
                      </Form.Item>
                      <div className="flex justify-center items-center">
                        <Form.Item
                          className="w-full"
                          label="Upload PDF"
                          name={[field.name, "Files"]}
                          // fieldKey={[field.fieldKey, 'Files']}
                          valuePropName="fileList"
                          getValueFromEvent={(e) => {
                            const list = Array.isArray(e)
                              ? e
                              : (e?.fileList ?? e ?? []);
                            return list.filter((f) => f?.status !== "removed");
                          }}
                          validateTrigger={["onChange"]}
                          rules={[
                            {
                              validator: (_, value) => {
                                const files = value || [];
                                const hasFileItem =
                                  Array.isArray(files) && files.length > 0;
                                const isEditMode = !!selectedTNC?.id;
                                const currentFileId = form.getFieldValue([
                                  "documents",
                                  field.name,
                                  "FileId",
                                ]);
                                const isExistingDoc = !!currentFileId;
                                if (!isEditMode || !isExistingDoc) {
                                  if (!hasFileItem) {
                                    return Promise.reject(
                                      new Error("Please upload PDF"),
                                    );
                                  }
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <Upload
                            beforeUpload={beforeUpload}
                            accept=".pdf"
                            maxCount={1}
                            className="min-w-full full-width-select"
                          >
                            <Button
                              icon={<UploadOutlined />}
                              size="large"
                              className="min-w-full"
                            >
                              {" "}
                              Upload PDF{" "}
                            </Button>
                          </Upload>
                        </Form.Item>

                        <Button
                          type="button"
                          disabled={index !== fields.length - 1 || index === 0}
                          onClick={() => remove(index)}
                          className={`mt-6 flex items-center justify-center border rounded-md  transition ${index !== fields.length - 1 || index === 0 ? "opacity-50 cursor-not-allowed " : ""}`}
                        >
                          <Delete />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {!selectedTNC && (
                    <div className="flex justify-start mt-3">
                      <button
                        type="button"
                        className="flex items-center gap-1 bg-primary text-white px-3 py-1.5 rounded-md"
                        onClick={() => {
                          add({ FileNames: "", Files: [] });
                        }}
                      >
                        <img
                          src="assets/icons/add_icon.svg"
                          alt="icon"
                          width={12}
                        />
                        Add{" "}
                      </button>
                    </div>
                  )}
                </>
              )}
            </Form.List>
          </div>
          <div className="flex justify-end gap-4">
            <button
              type="button"
              onClick={() => setEditModal({ open: false })}
              className="border border-primary primary rounded-md px-3"
            >
              Cancel
            </button>
            <LoadableButton
              type="submit"
              isLoading={submitLoading}
              lable={
                selectedTNC
                  ? "Update Terms & Conditions"
                  : "Create Terms & Conditions"
              }
              loadingLable={selectedTNC ? "Updating..." : "Creating..."}
              className="bg-primary text-white px-3 py-2 rounded-lg"
              size="large"
            />
          </div>
        </Form>
      </ModalComponent>
    </div>
  );
}
